/**
 * Convert a BRL value (number, or canonical string like "34,69" / "1.234,56" / "1234")
 * to its Brazilian Portuguese "extenso" representation.
 *
 * Examples:
 *   brlToExtenso("34,69")       -> "trinta e quatro reais e sessenta e nove centavos"
 *   brlToExtenso("1")           -> "um real"
 *   brlToExtenso("0,01")        -> "um centavo"
 *   brlToExtenso("1234,56")     -> "mil duzentos e trinta e quatro reais e cinquenta e seis centavos"
 *   brlToExtenso("1000000")     -> "um milhão de reais"
 *   brlToExtenso("")            -> ""
 *
 * Notes about Portuguese grammar implemented here:
 *   - "cem" is used for exactly 100; "cento" only when followed by units (101 = "cento e um").
 *   - "mil" has no "um" prefix (1000 = "mil", not "um mil").
 *   - The connector "e" between consecutive chunks is used when the lower chunk is
 *     < 100 OR is an exact multiple of 100 (e.g. "mil e cinco", "mil e duzentos"),
 *     otherwise the chunks are simply space-separated ("mil duzentos e trinta e quatro").
 *   - The particle "de" before "reais" is added only when the value is exactly
 *     N millions/billions/trillions (e.g. "um milhão de reais", "dois bilhões de reais").
 */

const UNIDADES = [
  "zero",
  "um",
  "dois",
  "três",
  "quatro",
  "cinco",
  "seis",
  "sete",
  "oito",
  "nove",
];

const DEZ_A_DEZENOVE = [
  "dez",
  "onze",
  "doze",
  "treze",
  "quatorze",
  "quinze",
  "dezesseis",
  "dezessete",
  "dezoito",
  "dezenove",
];

const DEZENAS = [
  "",
  "",
  "vinte",
  "trinta",
  "quarenta",
  "cinquenta",
  "sessenta",
  "setenta",
  "oitenta",
  "noventa",
];

const CENTENAS = [
  "",
  "cento",
  "duzentos",
  "trezentos",
  "quatrocentos",
  "quinhentos",
  "seiscentos",
  "setecentos",
  "oitocentos",
  "novecentos",
];

/** Scale words by chunk index (1 = thousands, 2 = millions, ...). */
const SCALES = [
  { singular: "", plural: "" },
  { singular: "mil", plural: "mil" },
  { singular: "milhão", plural: "milhões" },
  { singular: "bilhão", plural: "bilhões" },
  { singular: "trilhão", plural: "trilhões" },
];

/** Spell out an integer 0..999. Returns "" for 0. */
function chunkToExtenso(n) {
  if (n === 0) return "";
  if (n === 100) return "cem";

  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const parts = [];

  if (hundreds > 0) parts.push(CENTENAS[hundreds]);

  if (rest > 0) {
    if (rest < 10) {
      parts.push(UNIDADES[rest]);
    } else if (rest < 20) {
      parts.push(DEZ_A_DEZENOVE[rest - 10]);
    } else {
      const tens = Math.floor(rest / 10);
      const ones = rest % 10;
      parts.push(ones === 0 ? DEZENAS[tens] : `${DEZENAS[tens]} e ${UNIDADES[ones]}`);
    }
  }

  return parts.join(" e ");
}

/** Spell out a non-negative integer of arbitrary size (within supported scales). */
function intToExtenso(n) {
  if (!Number.isFinite(n) || n < 0) return "";
  n = Math.floor(n);
  if (n === 0) return "zero";

  /** chunks[0] = units, chunks[1] = thousands, chunks[2] = millions, ... */
  const chunks = [];
  let temp = n;
  while (temp > 0) {
    chunks.push(temp % 1000);
    temp = Math.floor(temp / 1000);
  }

  if (chunks.length > SCALES.length) {
    // Beyond what we support; fall back to digits to avoid mis-spelling.
    return String(n);
  }

  const segments = [];
  for (let i = chunks.length - 1; i >= 0; i--) {
    const value = chunks[i];
    if (value === 0) continue;

    const scale = SCALES[i];
    let text;
    if (i === 1) {
      text = value === 1 ? "mil" : `${chunkToExtenso(value)} mil`;
    } else if (i === 0) {
      text = chunkToExtenso(value);
    } else {
      const word = value === 1 ? scale.singular : scale.plural;
      text = `${chunkToExtenso(value)} ${word}`;
    }
    segments.push({ value, text });
  }

  let out = segments[0].text;
  for (let i = 1; i < segments.length; i++) {
    const v = segments[i].value;
    const useE = v < 100 || v % 100 === 0;
    out += useE ? ` e ${segments[i].text}` : ` ${segments[i].text}`;
  }
  return out;
}

function realWord(reais) {
  return reais === 1 ? "real" : "reais";
}

function centavoWord(cents) {
  return cents === 1 ? "centavo" : "centavos";
}

/** Returns " de" when the value is exactly N millions/billions/trillions. */
function deReaisQualifier(reais) {
  if (reais < 1_000_000) return "";
  let nonZeroChunks = 0;
  let temp = reais;
  while (temp > 0) {
    if (temp % 1000 !== 0) nonZeroChunks++;
    temp = Math.floor(temp / 1000);
  }
  return nonZeroChunks === 1 ? " de" : "";
}

function reaisToExtenso(reais) {
  if (reais === 0) return "";
  return `${intToExtenso(reais)}${deReaisQualifier(reais)} ${realWord(reais)}`;
}

function centavosToExtenso(cents) {
  if (cents === 0) return "";
  return `${intToExtenso(cents)} ${centavoWord(cents)}`;
}

/**
 * Parse the canonical form-state BRL value (string with "," decimal, optional "." thousands;
 * or a JS number) into integer reais and cents. Returns null when blank/invalid.
 */
function parseBrlValueToReaisCents(value) {
  if (value == null) return null;

  if (typeof value === "number") {
    if (!Number.isFinite(value) || value < 0) return null;
    const total = Math.round(value * 100);
    return { reais: Math.floor(total / 100), cents: total % 100 };
  }

  const raw = String(value).trim();
  if (raw === "") return null;

  /* Remove thousand separators; "," is the decimal separator in canonical form state. */
  const cleaned = raw.replace(/\./g, "");
  const [intStrRaw = "", fracStrRaw = ""] = cleaned.split(",");

  const intDigits = intStrRaw.replace(/\D/g, "");
  const fracDigits = fracStrRaw.replace(/\D/g, "").slice(0, 2).padEnd(fracStrRaw ? 2 : 0, "0");

  if (intDigits === "" && fracDigits === "") return null;

  const reais = intDigits === "" ? 0 : Number(intDigits);
  const cents = fracDigits === "" ? 0 : Number(fracDigits);

  if (!Number.isFinite(reais) || !Number.isFinite(cents)) return null;
  return { reais, cents };
}

/** Convert a BRL value (number or canonical string) to its Portuguese extenso form. */
export function brlToExtenso(value) {
  const parsed = parseBrlValueToReaisCents(value);
  if (!parsed) return "";

  const { reais, cents } = parsed;
  if (reais === 0 && cents === 0) return "zero reais";

  const parts = [];
  if (reais > 0) parts.push(reaisToExtenso(reais));
  if (cents > 0) parts.push(centavosToExtenso(cents));
  return parts.join(" e ");
}
